import apiService from './apiService';

class ShopifyBulkOperationsService {
  constructor(shop, accessToken) {
    console.log('Initializing ShopifyBulkOperationsService with shop:', shop);
    this.shop = shop;
    this.accessToken = accessToken;
    
    // Field mappings for Shopify API
    this.fieldMapping = {
      title: 'title',
      description: 'descriptionHtml',
      handle: 'handle',
      vendor: 'vendor',
      product_type: 'productType',
      productType: 'productType',
      tags: 'tags',
      status: 'status',
      images_alt_text: 'featuredImage.altText',
      variant_images_alt_text: 'media.alt',
      seo_title: 'seo.title',
      seo_description: 'seo.description',
      option1_name: 'options[0].name',
      option2_name: 'options[1].name',
      option3_name: 'options[2].name'
    };
  }

  async makeGraphQLRequest(query, variables = null, shopOverride = null, accessTokenOverride = null) {
    const shop = shopOverride || this.shop;
    const accessToken = accessTokenOverride || this.accessToken;
    
    if (!shop || !accessToken) {
      throw new Error('Shop and access token are required');
    }

    console.log('Sending GraphQL request:', { query, variables });

    try {
      // Use the apiService to make the GraphQL request
      const response = await apiService.graphql(query, variables, shop, accessToken);
      
      if (response.data.errors) {
        console.error('GraphQL errors:', response.data.errors);
        throw new Error(response.data.errors[0].message);
      }

      return response.data.data;
    } catch (error) {
      console.error('GraphQL request failed:', {
        error,
        query,
        variables,
        response: error.response?.data
      });
      throw error;
    }
  }

  setShopCredentials(shop, accessToken) {
    this.shop = shop;
    this.accessToken = accessToken;
  }

  async startBulkProductsQuery(query) {
    try {
      // First check if there's already an operation running
      const currentOperation = await this.checkBulkOperationStatus();
      
      // If there's an operation running, wait for it to complete or fail
      if (currentOperation && ['CREATED', 'RUNNING'].includes(currentOperation.status)) {
        console.log('Waiting for existing operation to complete...');
        try {
          await this.pollOperationStatus();
        } catch (error) {
          console.log('Previous operation failed or timed out, proceeding with new operation');
        }
      }

      console.log('Starting bulk operation with query:', query);
      
      const response = await this.makeGraphQLRequest(`
        mutation {
          bulkOperationRunQuery(
            query: """${query}"""
          ) {
            bulkOperation {
              id
              status
            }
            userErrors {
              field
              message
            }
          }
        }
      `);

      console.log('Bulk operation response:', JSON.stringify(response, null, 2));

      if (!response?.bulkOperationRunQuery) {
        throw new Error('Failed to start bulk operation');
      }

      const result = response.bulkOperationRunQuery;
      
      if (result.userErrors && result.userErrors.length > 0) {
        console.error('Bulk operation user errors:', result.userErrors);
        throw new Error(result.userErrors.map(error => error.message).join(', '));
      }

      if (!result.bulkOperation) {
        throw new Error('No bulk operation returned from query');
      }

      return result.bulkOperation;
    } catch (error) {
      console.error('Failed to start bulk operation:', error);
      throw error;
    }
  }

  async checkBulkOperationStatus() {
    try {
      const response = await this.makeGraphQLRequest(`
        query {
          currentBulkOperation {
            id
            status
            errorCode
            createdAt
            completedAt
            objectCount
            fileSize
            url
            partialDataUrl
          }
        }
      `);

      return response?.currentBulkOperation;
    } catch (error) {
      console.error('Error checking bulk operation status:', error);
      throw error;
    }
  }

  async pollOperationStatus(maxAttempts = 30, pollInterval = 2000) {
    console.log('Polling operation status...');
    let attempts = 0;
    
    while (attempts < maxAttempts) {
      attempts++;
      console.log(`Poll attempt ${attempts}/${maxAttempts}`);
      
      const operation = await this.checkBulkOperationStatus();
      
      if (!operation) {
        console.log('No operation found, assuming completed');
        return null;
      }
      
      console.log('Current operation status:', operation.status);
      
      if (['COMPLETED', 'FAILED', 'CANCELLED'].includes(operation.status)) {
        return operation;
      }
      
      if (operation.status === 'COMPLETED' && (operation.url || operation.partialDataUrl)) {
        return operation;
      }

      if (operation.status === 'COMPLETED' && !operation.url && !operation.partialDataUrl) {
        throw new Error('Operation completed but no URL provided');
      }

      // Wait before next poll
      await new Promise(resolve => setTimeout(resolve, pollInterval));
    }
  }

  async pollBulkOperation(operationId, maxAttempts = 30, interval = 2000) {
    console.log('Starting to poll bulk operation:', operationId);
    let attempts = 0;
    
    while (attempts < maxAttempts) {
      try {
        const response = await this.makeGraphQLRequest(`
          query {
            currentBulkOperation {
              id
              status
              errorCode
              createdAt
              completedAt
              objectCount
              fileSize
              url
              partialDataUrl
            }
          }
        `);
        
        console.log('Poll response:', response);
        
        if (!response.currentBulkOperation) {
          console.error('No current bulk operation found');
          return null;
        }
        
        const operation = response.currentBulkOperation;
        const status = {
          status: operation.status,
          objectCount: operation.objectCount,
          fileSize: operation.fileSize
        };
        
        console.log('Bulk operation status:', status);
        
        if (operation.status === 'COMPLETED') {
          console.log('Bulk operation completed successfully');
          const url = operation.url;
          if (!url) {
            console.warn('No URL in completed operation, trying to use partialDataUrl');
            if (operation.partialDataUrl) {
              return await this.downloadBulkOperationData(operation.partialDataUrl);
            }
            throw new Error('No URL in completed operation');
          }
          return await this.downloadBulkOperationData(url);
        } else if (operation.status === 'FAILED') {
          const errorCode = operation.errorCode || 'UNKNOWN_ERROR';
          console.error(`Bulk operation failed: ${errorCode}`);
          
          // Provide more detailed error information based on error code
          if (errorCode === 'ACCESS_DENIED') {
            throw new Error(`Bulk operation failed: ACCESS_DENIED - The app doesn't have permission to perform this operation for shop ${this.shop}. Please check your app's API access scopes.`);
          } else {
            throw new Error(`Bulk operation failed: ${errorCode}`);
          }
        } else if (operation.status === 'CANCELED') {
          console.error('Bulk operation was canceled');
          throw new Error('Bulk operation was canceled');
        }
        
        // If we get here, the operation is still in progress
        attempts++;
        await new Promise(resolve => setTimeout(resolve, interval));
      } catch (error) {
        console.error('Error polling bulk operation:', error);
        throw error;
      }
    }
    
    throw new Error(`Bulk operation polling timed out after ${maxAttempts} attempts`);
  }

  async downloadBulkOperationData(url) {
    try {
      console.log('Downloading bulk operation data from:', url);
      const response = await fetch(url);
      
      if (!response.ok) {
        throw new Error(`Failed to download bulk operation data: ${response.status} ${response.statusText}`);
      }
      
      const data = await response.text();
      console.log('Downloaded data size:', data.length);
      return data;
    } catch (error) {
      console.error('Error downloading bulk operation data:', error);
      throw error;
    }
  }

  processProductsData(jsonlData) {
    try {
      if (!jsonlData || jsonlData.trim() === '') {
        console.log('No data to process');
        return [];
      }
      
      const lines = jsonlData.trim().split('\n');
      const products = new Map(); // Use a map to group product data
      
      console.log('Processing JSONL data, total lines:', lines.length);

      for (const line of lines) {
        try {
          const data = JSON.parse(line);
          
          // Check if this is a product node
          if (data.id && data.id.includes('gid://shopify/Product/')) {
            // Filter out system metafields and only keep product-specific ones
            if (data.metafields) {
              console.log('Before filtering metafields:', data.metafields.edges.length);
              data.metafields = data.metafields.edges
                .map(edge => edge.node)
                .filter(metafield => 
                  metafield.id && 
                  !metafield.key.startsWith("gid://shopify/Metafield/") &&
                  metafield.key !== "null" &&
                  !metafield.key.includes("custom.custom.")
                );
              console.log('After filtering metafields:', data.metafields.length);
            }

            products.set(data.id, {
              ...data,
              variants: [],
              metafields: [],
              originalMetafields: data.metafields || [], // Store original metafields
              images: []
            });
          }
          // Check if this is a variant node
          else if (data.id && data.id.includes('gid://shopify/ProductVariant/') && data.__parentId) {
            const product = products.get(data.__parentId);
            if (product) {
              product.variants.push(data);
            }
          }
          // Check if this is a metafield
          else if (data.id && data.id.includes('gid://shopify/Metafield/') && data.__parentId) {
            const product = products.get(data.__parentId);
            if (product) {
              product.metafields.push(data);
            }
          }
          // Check if this is an image
          else if (data.id && data.id.includes('gid://shopify/ProductImage/') && data.__parentId) {
            const product = products.get(data.__parentId);
            if (product) {
              product.images.push(data);
            }
          }
        } catch (error) {
          console.error('Error processing JSONL line:', error);
          // Continue with next line
        }
      }
      
      // Convert map to array and process each product
      const processedProducts = Array.from(products.values()).map(product => {
        // Process images
        if (product.images && product.images.length > 0) {
          // Sort images by position
          product.images.sort((a, b) => (a.position || 0) - (b.position || 0));
          
          // Set featured image if not already set
          if (!product.featuredImage && product.images.length > 0) {
            product.featuredImage = product.images[0];
          }
        }
        
        // Process variants
        if (product.variants && product.variants.length > 0) {
          // Sort variants by position
          product.variants.sort((a, b) => (a.position || 0) - (b.position || 0));
          
          // Process variant images
          product.variants.forEach(variant => {
            if (variant.image && variant.image.id) {
              // Find the full image data from product images
              const fullImage = product.images.find(img => img.id === variant.image.id);
              if (fullImage) {
                variant.image = fullImage;
              }
            }
          });
        }
        
        return product;
      });
      
      console.log(`Processed ${processedProducts.length} products`);
      return processedProducts;
    } catch (error) {
      console.error('Error processing products data:', error);
      throw error;
    }
  }

  async getMetafieldDefinitions() {
    try {
      const response = await this.makeGraphQLRequest(`
        query {
          metafieldDefinitions(
            first: 250,
            ownerType: PRODUCT
          ) {
            nodes {
              id
              name
              namespace
              key
              description
              type {
                name
              }
              validations {
                name
                value
              }
              ownerType
            }
          }
        }
      `);

      if (!response?.metafieldDefinitions?.nodes) {
        console.error('No metafield definitions found:', response);
        return [];
      }

      const definitions = response.metafieldDefinitions.nodes;
      console.log('Found metafield definitions:', definitions);
      return definitions;
    } catch (error) {
      console.error('Error fetching metafield definitions:', error);
      return [];
    }
  }

  async enrichProductsWithMetafieldDefinitions(products) {
    try {
      // Get all metafield definitions for products
      const metafieldDefinitions = await this.getMetafieldDefinitions();
      
      if (!metafieldDefinitions || metafieldDefinitions.length === 0) {
        console.log('No metafield definitions found, returning original products');
        return products;
      }
      
      console.log(`Enriching ${products.length} products with ${metafieldDefinitions.length} metafield definitions`);
      
      // Process each product
      return products.map(product => {
        // Create a map of existing metafields by namespace.key
        const existingMetafields = {};
        if (product.metafields && Array.isArray(product.metafields)) {
          product.metafields.forEach(metafield => {
            const key = `${metafield.namespace}.${metafield.key}`;
            existingMetafields[key] = metafield;
          });
        }
        
        // Create a new array with all metafield definitions, using existing values where available
        const enrichedMetafields = metafieldDefinitions.map(definition => {
          const key = `${definition.namespace}.${definition.key}`;
          
          // If we have an existing metafield with this namespace and key, use its value
          if (existingMetafields[key]) {
            return existingMetafields[key];
          }
          
          // Otherwise create a new metafield with empty value
          return {
            id: null, // No ID for new metafields
            namespace: definition.namespace,
            key: definition.key,
            value: null,
            type: definition.type.name,
            name: definition.name,
            description: definition.description
          };
        });
        
        // Return the product with enriched metafields
        return {
          ...product,
          metafields: enrichedMetafields,
          // Keep the original metafields for reference
          originalMetafields: product.metafields || []
        };
      });
    } catch (error) {
      console.error('Error enriching products with metafield definitions:', error);
      return products;
    }
  }

  static buildFullProductQueryForIds(productIds) {
    // If productIds is a string, convert it to an array
    const ids = Array.isArray(productIds) ? productIds : [productIds];
    
    // Build the ID filter
    const idFilter = ids.map(id => {
      // Strip any existing gid:// prefix to ensure consistent formatting
      const cleanId = id.replace('gid://shopify/Product/', '');
      return `id:${cleanId}`;
    }).join(' OR ');

    console.log('Querying products with filter:', idFilter);

    return `
      {
        products(query: "${idFilter}", first: 250) {
          edges {
            node {
              id
              title
              handle
              descriptionHtml
              vendor
              productType
              status
              tags
              publishedAt
              createdAt
              updatedAt
              onlineStoreUrl
              hasOnlyDefaultVariant
              requiresSellingPlan
              seo {
                title
                description
              }
              options {
                id
                name
                position
                values
              }
              priceRangeV2 {
                maxVariantPrice {
                  amount
                  currencyCode
                }
                minVariantPrice {
                  amount
                  currencyCode
                }
              }
              variants(first: 1000) {
                edges {
                  node {
                    id
                    title
                    sku
                    price
                    compareAtPrice
                    inventoryQuantity
                    selectedOptions {
                      name
                      value
                    }
                    inventoryItem {
                      id
                      sku
                      tracked
                      inventoryLevels(first: 1) {
                        edges {
                          node {
                            id
                            quantities(names: ["available"]) {
                              name
                              quantity
                            }
                            location {
                              id
                              name
                              isActive
                            }
                          }
                        }
                      }
                    }
                    barcode
                    taxable
                    position
                    image {
                      id
                      url
                      altText
                      width
                      height
                    }
                  }
                }
              }
              metafields(first: 100) {
                edges {
                  node {
                    id
                    namespace
                    key
                    value
                    type
                  }
                }
              }
              images(first: 250) {
                edges {
                  node {
                    id
                    url
                    altText
                    width
                    height
                  }
                }
              }
              featuredImage {
                id
                url
                altText
                width
                height
              }
            }
          }
        }
      }
    `;
  }

  async deleteProductImage(productId, imageId) {
    try {
      console.log('Deleting product image:', { productId, imageId });
      
      // Format the IDs if needed
      const formattedProductId = productId.includes('gid://') ? productId : `gid://shopify/Product/${productId}`;
      const formattedImageId = imageId.includes('gid://') ? imageId : `gid://shopify/ProductImage/${imageId}`;
      
      // First, get the product to find all current images
      const productQuery = `
        query getProduct($id: ID!) {
          product(id: $id) {
            id
            media(first: 250) {
              edges {
                node {
                  id
                  mediaContentType
                  alt
                  ... on MediaImage {
                    image {
                      originalSrc
                    }
                  }
                }
              }
            }
          }
        }
      `;
      
      const productResult = await this.makeGraphQLRequest(productQuery, { id: formattedProductId });
      
      if (!productResult || !productResult.product) {
        throw new Error('Failed to retrieve product');
      }
      
      // Get all media IDs except the one we want to delete
      const currentMedia = productResult.product.media.edges
        .filter(edge => edge.node.id !== formattedImageId)
        .map(edge => ({
          id: edge.node.id,
          alt: edge.node.alt || '',
          mediaContentType: edge.node.mediaContentType
        }));
      
      // Create a mutation to update the product with the filtered media list
      const mutation = `
        mutation productUpdate($input: ProductInput!) {
          productUpdate(input: $input) {
            product {
              id
              media(first: 10) {
                edges {
                  node {
                    id
                  }
                }
              }
            }
            userErrors {
              field
              message
            }
          }
        }
      `;
      
      // Prepare the variables for the mutation
      const variables = {
        input: {
          id: formattedProductId,
          media: currentMedia
        }
      };

      const result = await this.makeGraphQLRequest(mutation, variables);
      
      if (result?.productUpdate?.userErrors?.length > 0) {
        const errors = result.productUpdate.userErrors;
        throw new Error(`Failed to delete product image: ${errors.map(e => e.message).join(', ')}`);
      }
      
      // Get the newly created image (should be the last one)
      const newImageNode = result?.productUpdate?.product?.images?.edges?.[0]?.node;
      
      if (!newImageNode) {
        throw new Error('Failed to retrieve the newly created image');
      }
      
      return {
        success: true,
        data: result.productUpdate.product
      };
    } catch (error) {
      console.error('Error deleting product image:', error);
      throw error;
    }
  }

  async getProductImages(productId) {
    try {
      const query = `
        query getProductImages($productId: ID!) {
          product(id: $productId) {
            images(first: 50) {
              edges {
                node {
                  id
                  src
                  altText
                  position
                  width
                  height
                }
              }
            }
          }
        }
      `;

      // Ensure productId is properly formatted
      const formattedProductId = productId.includes('gid://') ? productId : `gid://shopify/Product/${productId}`;
      
      const variables = {
        productId: formattedProductId
      };

      console.log('Fetching product images with variables:', variables);
      const result = await this.makeGraphQLRequest(query, variables);
      
      if (!result?.product?.images?.edges) {
        return [];
      }
      
      return result.product.images.edges.map(edge => edge.node);
    } catch (error) {
      console.error('Error fetching product images:', error);
      return [];
    }
  }

  async createProductImage(productId, imageData) {
    try {
      // Updated to use productUpdate mutation for 2025-01 API version
      const mutation = `
        mutation productUpdate($input: ProductInput!) {
          productUpdate(input: $input) {
            product {
              id
              images(last: 1) {
                edges {
                  node {
                    id
                    src
                    altText
                    width
                    height
                  }
                }
              }
            }
            userErrors {
              field
              message
            }
          }
        }
      `;

      // Format product ID if needed
      const graphqlProductId = productId.includes('gid://') ? productId : `gid://shopify/Product/${productId}`;
      
      // First, get the current product images
      const productImages = await this.getProductImages(graphqlProductId);
      
      // Create a new image object
      const newImage = {
        src: imageData.src,
        altText: imageData.alt || '',
        position: imageData.position || (productImages.length + 1)
      };
      
      // Add the new image to the existing images
      const updatedImages = [...productImages.map(img => ({
        id: img.id,
        src: img.src,
        altText: img.altText || '',
        position: img.position || 1
      })), newImage];
      
      // Create variables for the mutation
      const variables = {
        input: {
          id: graphqlProductId,
          images: updatedImages
        }
      };

      const result = await this.makeGraphQLRequest(mutation, variables);
      
      if (result?.productUpdate?.userErrors?.length > 0) {
        const errors = result.productUpdate.userErrors;
        throw new Error(`Failed to create image: ${errors.map(e => e.message).join(', ')}`);
      }
      
      // Get the newly created image (should be the last one)
      const newImageNode = result?.productUpdate?.product?.images?.edges?.[0]?.node;
      
      if (!newImageNode) {
        throw new Error('Failed to retrieve the newly created image');
      }
      
      return {
        success: true,
        image: newImageNode
      };
    } catch (error) {
      console.error('Error creating product image via GraphQL:', error);
      throw error;
    }
  }

  async updateProductImage(productId, originalImage, processedImage) {
    try {
      console.log('Updating product image with:', { productId, originalImage, processedImage });
      
      // Ensure we have a valid product ID
      if (!productId) {
        throw new Error('Product ID is required for updating images');
      }
      
      // Format product ID if needed
      const formattedProductId = productId.includes('gid://') ? productId : `gid://shopify/Product/${productId}`;
      
      // Initialize the batch processing collections if they don't exist
      if (!this.constructor._batchImageUploads) {
        this.constructor._batchImageUploads = new Map();
      }
      
      // Get or create the batch for this product
      if (!this.constructor._batchImageUploads.has(formattedProductId)) {
        this.constructor._batchImageUploads.set(formattedProductId, {
          images: [],
          variantMap: {}
        });
      }
      
      const batch = this.constructor._batchImageUploads.get(formattedProductId);
      
      // Add this image to the batch
      const position = batch.images.length + 1;
      batch.images.push({
        alt: originalImage.alt || '',
        src: processedImage.processedUrl || processedImage.url
      });
      
      // If this image has variant associations, add them to the map
      if (originalImage.variant_ids && originalImage.variant_ids.length > 0) {
        batch.variantMap[position] = originalImage.variant_ids;
        console.log(`Added variant associations for position ${position} in batch`);
      }
      
      console.log(`Added image to batch for product ${formattedProductId}, position ${position}`);
      
      // For now, just return success - actual processing will happen in bulk
      return {
        success: true,
        batchSize: batch.images.length
      };
    } catch (error) {
      console.error('Error adding image to batch:', error);
      throw error;
    }
  }
  
  /**
   * Process all batched images for a product
   * @param {string} productId - The ID of the product
   * @returns {Promise<object>} Result of the batch processing
   */
  async processBatchedImages(productId) {
    try {
      // Format product ID if needed
      const formattedProductId = productId.includes('gid://') ? productId : `gid://shopify/Product/${productId}`;
      
      // Check if we have any batched images for this product
      if (!this.constructor._batchImageUploads || !this.constructor._batchImageUploads.has(formattedProductId)) {
        console.log(`No batched images found for product ${formattedProductId}`);
        return { success: true, processed: 0 };
      }
      
      const batch = this.constructor._batchImageUploads.get(formattedProductId);
      console.log(`Processing batch of ${batch.images.length} images for product ${formattedProductId}`);
      
      // Process the batch using the bulk method
      const result = await this.processProductImages(
        formattedProductId,
        batch.images,
        batch.variantMap,
        true // Replace existing images
      );
      
      // Clear the batch after processing
      this.constructor._batchImageUploads.delete(formattedProductId);
      
      return result;
    } catch (error) {
      console.error('Error processing batched images:', error);
      throw error;
    }
  }

  /**
   * Check if a product has any batched images and process them if needed
   * This method should be called after all images for a product have been added to the batch
   * @param {string} productId - The ID of the product
   * @param {string} shop - The shop domain
   * @param {string} accessToken - The shop access token
   * @returns {Promise<boolean>} True if batch was processed, false if no batch was found
   */
  static async triggerBatchProcessing(productId, shop, accessToken) {
    try {
      const service = new ShopifyBulkOperationsService(shop, accessToken);
      const formattedProductId = productId.includes('gid://') ? productId : `gid://shopify/Product/${productId}`;
      
      // Check if we have any batched images for this product
      if (!service.constructor._batchImageUploads || !service.constructor._batchImageUploads.has(formattedProductId)) {
        console.log(`No batched images to process for product ${formattedProductId}`);
        return false;
      }
      
      const batchSize = service.constructor._batchImageUploads.get(formattedProductId).images.length;
      console.log(`Triggering batch processing for ${batchSize} images for product ${formattedProductId}`);
      
      // Process the batch
      const result = await service.processBatchedImages(formattedProductId);
      console.log(`Batch processing completed for product ${formattedProductId}:`, result);
      
      return true;
    } catch (error) {
      console.error('Error triggering batch processing:', error);
      return false;
    }
  }

  async associateImageWithVariants(productId, mediaId, variantIds) {
    try {
      // Format the variant IDs if needed
      const formattedVariantIds = variantIds.map(id => {
        return id.includes('gid://') ? id : `gid://shopify/ProductVariant/${id}`;
      });
      
      // Format the media ID if needed
      const formattedMediaId = mediaId.includes('gid://') ? mediaId : `gid://shopify/MediaImage/${mediaId}`;
      
      console.log(`Associating media ${formattedMediaId} with ${formattedVariantIds.length} variants`);
      
      // In Shopify API 2025-01, we need to use productVariantsBulkUpdate instead of productVariantUpdate
      const variantUpdateMutation = `
        mutation productVariantsBulkUpdate($productId: ID!, $variants: [ProductVariantsBulkInput!]!) {
          productVariantsBulkUpdate(productId: $productId, variants: $variants) {
            productVariants {
              id
              media(first: 250) {
                edges {
                  node {
                    id
                  }
                }
              }
            }
            userErrors {
              field
              message
              code
            }
          }
        }
      `;
      
      // Prepare the variables for the mutation
      const variants = formattedVariantIds.map(variantId => ({
        id: variantId,
        mediaId: formattedMediaId // Use mediaId (singular) as per Shopify API docs
      }));
      
      const variantUpdateVariables = {
        productId: productId.includes('gid://') ? productId : `gid://shopify/Product/${productId}`,
        variants: variants
      };
      
      console.log(`Updating ${variants.length} variants with image ${formattedMediaId}`);
      console.log('Mutation variables:', JSON.stringify(variantUpdateVariables, null, 2));
      
      // Execute the mutation
      const variantUpdateResult = await this.makeGraphQLRequest(variantUpdateMutation, variantUpdateVariables);
      
      if (variantUpdateResult?.productVariantsBulkUpdate?.userErrors?.length > 0) {
        const errors = variantUpdateResult.productVariantsBulkUpdate.userErrors;
        console.error(`Error updating variants with image:`, errors);
        
        // Provide more detailed error information
        const errorDetails = errors.map(err => `Field: ${err.field || 'unknown'}, Message: ${err.message}, Code: ${err.code || 'unknown'}`).join('\n');
        console.error(`Detailed error information:\n${errorDetails}`);
        
        // Check for specific error types and provide guidance
        const invalidFieldError = errors.find(err => err.message.includes('Field is not defined'));
        if (invalidFieldError) {
          console.error('This appears to be an API schema error. The field name might have changed in the Shopify API.');
          console.error('Please check the latest Shopify API documentation for the correct field name.');
        }
        
        // Continue even if there are errors
      } else {
        console.log(`Successfully updated variants with image ${formattedMediaId}`);
        
        // Log the response for debugging
        if (variantUpdateResult?.productVariantsBulkUpdate?.productVariants) {
          const updatedVariants = variantUpdateResult.productVariantsBulkUpdate.productVariants;
          console.log(`Updated ${updatedVariants.length} variants with new media associations`);
        }
      }
      
      // Note: Due to API limitations, we can't directly verify the media-variant associations
      // The user may need to manually check or use the Shopify Admin UI
      
      return true;
    } catch (error) {
      console.error('Error associating image with variants:', error);
      throw error;
    }
  }

  async getProductImageUpdateStatus(taskId) {
    // This is a simplified version since we're not actually using a task system anymore
    // We're just returning a completed status immediately
    return {
      data: {
        status: 'completed',
        progress: 100,
        message: 'Image update completed successfully'
      }
    };
  }

  static async deleteProductImage(shop, accessToken, productId, imageId) {
    const service = new ShopifyBulkOperationsService(shop, accessToken);
    return service.deleteProductImage(productId, imageId);
  }

  static async createProductImage(shop, accessToken, productId, imageData) {
    const service = new ShopifyBulkOperationsService(shop, accessToken);
    return service.createProductImage(productId, imageData);
  }

  static async updateProductImage(shop, accessToken, productId, originalImage, processedImage) {
    const service = new ShopifyBulkOperationsService(shop, accessToken);
    return service.updateProductImage(productId, originalImage, processedImage);
  }

  static async getProductImageUpdateStatus(shop, accessToken, taskId) {
    const service = new ShopifyBulkOperationsService(shop, accessToken);
    return service.getProductImageUpdateStatus(taskId);
  }

  /**
   * Deletes specific media items from a product
   * @param {string} productId - The ID of the product
   * @param {Array} mediaIds - Array of media IDs to delete
   * @returns {Promise<object>} Result of the deletion operation
   */
  async deleteProductMedia(productId, mediaIds) {
    try {
      console.log('Deleting product media:', { productId, mediaIds });
      
      // Ensure we have a valid product ID and media IDs
      if (!productId) {
        throw new Error('Product ID is required for deleting media');
      }
      
      if (!mediaIds || !Array.isArray(mediaIds) || mediaIds.length === 0) {
        console.warn('No media IDs provided for deletion');
        return { success: true, deletedCount: 0 };
      }
      
      // Format product ID if needed
      const formattedProductId = productId.includes('gid://') ? productId : `gid://shopify/Product/${productId}`;
      
      // Format media IDs if needed
      const formattedMediaIds = mediaIds.map(id => {
        return id.includes('gid://') ? id : `gid://shopify/Media/${id}`;
      });
      
      console.log(`Deleting ${formattedMediaIds.length} media items from product ${formattedProductId}`);
      
      // Create the mutation for deleting media
      const deleteMediaMutation = `
        mutation productDeleteMedia($productId: ID!, $mediaIds: [ID!]!) {
          productDeleteMedia(productId: $productId, mediaIds: $mediaIds) {
            deletedMediaIds
            userErrors {
              field
              message
            }
          }
        }
      `;
      
      const deleteMediaVariables = {
        productId: formattedProductId,
        mediaIds: formattedMediaIds
      };
      
      // Execute the mutation
      const deleteMediaResult = await this.makeGraphQLRequest(deleteMediaMutation, deleteMediaVariables);
      
      // Check for errors
      if (deleteMediaResult?.productDeleteMedia?.userErrors?.length > 0) {
        const errors = deleteMediaResult.productDeleteMedia.userErrors;
        console.error('Error deleting product media:', errors);
        throw new Error(`Failed to delete media: ${errors.map(e => e.message).join(', ')}`);
      }
      
      // Get the IDs of the deleted media
      const deletedMediaIds = deleteMediaResult?.productDeleteMedia?.deletedMediaIds || [];
      
      console.log(`Successfully deleted ${deletedMediaIds.length} media items`);
      
      return {
        success: true,
        deletedCount: deletedMediaIds.length,
        deletedMediaIds
      };
    } catch (error) {
      console.error('Error deleting product media:', error);
      throw error;
    }
  }

  /**
   * Deletes multiple media items using the fileDelete mutation
   * @param {Array<string>} mediaIds - Array of media IDs to delete (format: "gid://shopify/MediaImage/123456789")
   * @returns {Promise<object>} Result of the deletion operation
   */
  async deleteMediaItems(mediaIds) {
    try {
      if (!mediaIds || !Array.isArray(mediaIds) || mediaIds.length === 0) {
        console.log('No media IDs provided for deletion');
        return { success: true, deletedCount: 0 };
      }

      console.log(`Deleting ${mediaIds.length} media items`);
      
      // Format media IDs if needed
      const formattedMediaIds = mediaIds.map(id => {
        return id.includes('gid://') ? id : `gid://shopify/MediaImage/${id}`;
      });
      
      const deleteMediaMutation = `
        mutation fileDelete($input: [ID!]!) {
          fileDelete(fileIds: $input) {
            deletedFileIds
            userErrors {
              field
              message
            }
          }
        }
      `;
      
      const deleteMediaVariables = {
        input: formattedMediaIds
      };
      
      const result = await this.makeGraphQLRequest(deleteMediaMutation, deleteMediaVariables);
      
      if (result?.fileDelete?.userErrors?.length > 0) {
        const errors = result.fileDelete.userErrors;
        console.error('Error deleting media:', errors);
        throw new Error(`Failed to delete media: ${errors.map(e => e.message).join(', ')}`);
      }
      
      const deletedIds = result?.fileDelete?.deletedFileIds || [];
      console.log(`Successfully deleted ${deletedIds.length} media items`);
      
      return {
        success: true,
        deletedCount: deletedIds.length,
        deletedIds
      };
    } catch (error) {
      console.error('Error deleting media items:', error);
      throw error;
    }
  }

  /**
   * Static method for deleting media items
   * @param {Array<string>} mediaIds - Array of media IDs to delete
   * @returns {Promise<object>} Result of the deletion operation
   */
  static async deleteMediaItems(mediaIds) {
    const service = new ShopifyBulkOperationsService();
    return service.deleteMediaItems(mediaIds);
  }

  async processProductImages(productId, imagesToUpload = [], imageVariantMap = {}, replaceExisting = true) {
    try {
      console.log('Processing product images:', { 
        productId, 
        uploadCount: imagesToUpload.length,
        associationCount: Object.keys(imageVariantMap).length,
        replaceExisting
      });
      
      if (!imagesToUpload || imagesToUpload.length === 0) {
        console.log('No images to upload');
        return { success: true, uploadedCount: 0 };
      }
      
      // Format product ID if needed
      const formattedProductId = productId.includes('gid://') ? productId : `gid://shopify/Product/${productId}`;
      
      // First, get existing media IDs if we need to replace them
      let existingMediaIds = [];
      if (replaceExisting) {
        try {
          // Query to get existing media
          const getProductMediaQuery = `
            query GetProductMedia($productId: ID!) {
              product(id: $productId) {
                media(first: 250) {
                  edges {
                    node {
                      id
                    }
                  }
                }
              }
            }
          `;
          
          const getMediaVariables = {
            productId: formattedProductId
          };
          
          const mediaResult = await this.makeGraphQLRequest(getProductMediaQuery, getMediaVariables);
          
          if (mediaResult?.product?.media?.edges) {
            existingMediaIds = mediaResult.product.media.edges.map(edge => edge.node.id);
            console.log(`Found ${existingMediaIds.length} existing media items to replace`);
          }
        } catch (error) {
          console.warn('Error fetching existing media, will continue with upload:', error);
        }
      }
      
      // Step 1: Upload all images in a single operation
      console.log(`Uploading ${imagesToUpload.length} images for product ${formattedProductId}`);
      
      // Create the media input from the image data, preserving order
      const mediaInput = imagesToUpload.map((img, index) => ({
        alt: img.alt || '', // Ensure alt text is preserved
        mediaContentType: 'IMAGE',
        originalSource: img.src || img.url
      }));
      
      // Store the original positions and metadata for later reference
      const originalPositions = imagesToUpload.map((img, index) => ({
        position: img.position || index + 1, // Use the image's position if available, otherwise use index
        alt: img.alt || '',
        variant_ids: img.variant_ids || [] // Store variant IDs for later association
      }));
      
      // Create the mutation for creating media
      const createMediaMutation = `
        mutation productCreateMedia($productId: ID!, $media: [CreateMediaInput!]!) {
          productCreateMedia(productId: $productId, media: $media) {
            media {
              ... on MediaImage {
                id
                image {
                  id
                  url
                  altText
                }
              }
            }
            mediaUserErrors {
              field
              message
            }
          }
        }
      `;
      
      const createMediaVariables = {
        productId: formattedProductId,
        media: mediaInput
      };
      
      console.log('Sending productCreateMedia mutation with variables:', createMediaVariables);
      
      // Execute the mutation
      const createMediaResult = await this.makeGraphQLRequest(createMediaMutation, createMediaVariables);
      
      // Check for errors
      if (createMediaResult?.productCreateMedia?.mediaUserErrors?.length > 0) {
        const errors = createMediaResult.productCreateMedia.mediaUserErrors;
        console.error('Error creating product media:', errors);
        throw new Error(`Failed to create media: ${errors.map(e => e.message).join(', ')}`);
      }
      
      // Get the uploaded media
      const uploadedMedia = createMediaResult?.productCreateMedia?.media || [];
      console.log(`Successfully uploaded ${uploadedMedia.length} media items`);
      
      // Step 2: Associate images with variants if needed
      const variantUpdates = [];
      const processedVariants = new Set();
      
      // Map original image positions to new media IDs
      const positionToMediaIdMap = {};
      uploadedMedia.forEach((media, index) => {
        // Use the index + 1 to match the original positions we stored earlier
        positionToMediaIdMap[index + 1] = media.id;
        console.log(`Mapped position ${index + 1} to media ID ${media.id}`);
      });
      
      // Check if we have any variant associations in the original images
      let hasVariantAssociations = false;
      originalPositions.forEach(originalImage => {
        if (originalImage.variant_ids && originalImage.variant_ids.length > 0) {
          hasVariantAssociations = true;
        }
      });
      
      // If we have no variant associations in the original images, check if they might be in the metadata
      if (!hasVariantAssociations) {
        console.log('No variant associations found in original images, checking metadata...');
        imagesToUpload.forEach(img => {
          // Check if variant_ids exists in the metadata
          if (img.metadata?.shopifyMetadata?.variant_ids && img.metadata.shopifyMetadata.variant_ids.length > 0) {
            console.log(`Found variant_ids in metadata for image: ${img.metadata.shopifyMetadata.variant_ids.join(', ')}`);
            hasVariantAssociations = true;
            
            // Add these to the originalPositions array to be processed later
            const position = img.metadata.shopifyMetadata.position;
            if (position) {
              // Find if we already have this position in originalPositions
              const existingIndex = originalPositions.findIndex(img => img.position === position);
              if (existingIndex >= 0) {
                // Update the existing entry with variant_ids
                originalPositions[existingIndex].variant_ids = img.metadata.shopifyMetadata.variant_ids;
                console.log(`Updated variant_ids for position ${position} in originalPositions`);
              }
            }
          }
        });
      }
      
      // Process variant associations from the imageVariantMap
      Object.entries(imageVariantMap).forEach(([imageKey, variantIds]) => {
        if (!variantIds || !Array.isArray(variantIds) || variantIds.length === 0) {
          return;
        }
        
        // Improved position extraction from imageKey
        let position;
        if (typeof imageKey === 'number') {
          // If imageKey is already a number, use it directly
          position = imageKey;
        } else if (typeof imageKey === 'string') {
          // Try to extract position from string
          // First check if it's a simple numeric string
          if (/^\d+$/.test(imageKey)) {
            position = parseInt(imageKey);
          } else if (imageKey.includes('gid://')) {
            // If it's a Shopify GID, log it and try to find by ID directly
            console.log(`Image key is a Shopify GID: ${imageKey}`);
            // For GIDs, we might need to look up by ID instead of position
            // This is a fallback that might not always work
            const matchingMedia = uploadedMedia.find(media => 
              media.id === imageKey || 
              (media.image && media.image.id === imageKey)
            );
            if (matchingMedia) {
              // If we found a direct match, use its ID
              const mediaId = matchingMedia.id;
              // Associate variants with this media
              variantIds.forEach(variantId => {
                if (!processedVariants.has(variantId)) {
                  processedVariants.add(variantId);
                  variantUpdates.push({
                    id: variantId,
                    mediaId: mediaId // Use mediaId (singular) as per Shopify API docs
                  });
                }
              });
              return; // Skip the rest of this iteration
            } else {
              // If no direct match, try to extract a position from the ID
              const positionMatch = imageKey.match(/(\d+)$/);
              position = positionMatch ? parseInt(positionMatch[1]) : null;
            }
          } else {
            // Last resort: try to extract any number from the string
            const numberMatch = imageKey.match(/\d+/);
            position = numberMatch ? parseInt(numberMatch[0]) : null;
          }
        }
        
        // If we couldn't determine a position, log and skip
        if (!position) {
          console.warn(`Could not determine position from image key: ${imageKey}`);
          return;
        }
        
        const mediaId = positionToMediaIdMap[position];
        
        if (!mediaId) {
          console.warn(`No media found for position ${position}`);
          return;
        }
        
        // Add each variant to the update list if not already processed
        variantIds.forEach(variantId => {
          if (!processedVariants.has(variantId)) {
            processedVariants.add(variantId);
            variantUpdates.push({
              id: variantId,
              mediaId: mediaId // Use mediaId (singular) as per Shopify API docs
            });
          }
        });
      });
      
      // Also process variant associations from the original images as a fallback
      // This ensures we preserve variant associations even if not explicitly provided in imageVariantMap
      originalPositions.forEach((originalImage, index) => {
        if (!originalImage.variant_ids || originalImage.variant_ids.length === 0) {
          return;
        }
        
        const position = originalImage.position;
        const mediaId = positionToMediaIdMap[position];
        
        if (!mediaId) {
          console.warn(`No media found for position ${position} from original image`);
          return;
        }
        
        // Add each variant to the update list if not already processed
        originalImage.variant_ids.forEach(variantId => {
          if (!processedVariants.has(variantId)) {
            processedVariants.add(variantId);
            variantUpdates.push({
              id: variantId,
              mediaId: mediaId // Use mediaId (singular) as per Shopify API docs
            });
            console.log(`Added variant association from original image: variant ${variantId} -> media ${mediaId}`);
          }
        });
      });
      
      // If we have variant updates, perform them
      if (variantUpdates.length > 0) {
        console.log(`Updating ${variantUpdates.length} variants with image associations`);
        
        const updateVariantsMutation = `
          mutation productVariantsBulkUpdate($productId: ID!, $variants: [ProductVariantsBulkInput!]!) {
            productVariantsBulkUpdate(productId: $productId, variants: $variants) {
              productVariants {
                id
                media(first: 250) {
                  edges {
                    node {
                      id
                    }
                  }
                }
              }
              userErrors {
                field
                message
              }
            }
          }
        `;
        
        const updateVariantsVariables = {
          productId: formattedProductId,
          variants: variantUpdates
        };
        
        const updateVariantsResult = await this.makeGraphQLRequest(updateVariantsMutation, updateVariantsVariables);
        
        if (updateVariantsResult?.productVariantsBulkUpdate?.userErrors?.length > 0) {
          const errors = updateVariantsResult.productVariantsBulkUpdate.userErrors;
          console.error('Error updating variants with images:', errors);
          throw new Error(`Failed to update variants: ${errors.map(e => e.message).join(', ')}`);
        }
        
        console.log('Successfully updated variants with images');
      }
      
      // Step 3: Unlink old media if replaceExisting is true and we have existing media
      if (replaceExisting && existingMediaIds.length > 0) {
        try {
          console.log(`Unlinking ${existingMediaIds.length} old media items from product ${formattedProductId}`);
          
          // Unlink old media using the fileUpdate mutation instead of deleting them
          const unlinkResult = await this.unlinkMediaFromProduct(productId, existingMediaIds);
          
          console.log(`Successfully unlinked ${unlinkResult.unlinkedCount} old media items from product ${formattedProductId}`);
        } catch (unlinkError) {
          // Log the error but don't fail the entire operation
          console.error('Error unlinking old media:', unlinkError);
        }
      }
      
      return {
        success: true,
        uploadedCount: uploadedMedia.length,
        updatedVariantsCount: variantUpdates.length,
        unlinkedMediaCount: replaceExisting ? existingMediaIds.length : 0
      };
    } catch (error) {
      console.error('Error processing product images:', error);
      throw error;
    }
  }

  /**
   * Static method for processing product images
   * @param {string} productId - The ID of the product
   * @param {Array} imagesToUpload - Array of image data to upload
   * @param {Object} imageVariantMap - Mapping of image IDs to variant IDs for association
   * @param {boolean} replaceExisting - Whether to replace all existing images (default: true)
   * @returns {Promise<object>} Result of the image processing operation
   */
  static async processProductImages(productId, imagesToUpload = [], imageVariantMap = {}, replaceExisting = true) {
    const service = new ShopifyBulkOperationsService();
    return service.processProductImages(productId, imagesToUpload, imageVariantMap, replaceExisting);
  }

  /**
   * Unlinks media items from a product using the fileUpdate mutation
   * @param {string} productId - The ID of the product to unlink media from
   * @param {Array<string>} mediaIds - Array of media IDs to unlink (format: "gid://shopify/MediaImage/123456789")
   * @returns {Promise<object>} Result of the unlinking operation
   */
  async unlinkMediaFromProduct(productId, mediaIds) {
    try {
      if (!mediaIds || !Array.isArray(mediaIds) || mediaIds.length === 0) {
        console.log('No media IDs provided for unlinking');
        return { success: true, unlinkedCount: 0 };
      }

      // Format product ID if needed
      const formattedProductId = productId.includes('gid://') ? productId : `gid://shopify/Product/${productId}`;
      console.log(`Unlinking ${mediaIds.length} media items from product ${formattedProductId}`);
      
      // Format media IDs if needed
      const formattedMediaIds = mediaIds.map(id => {
        return id.includes('gid://') ? id : `gid://shopify/MediaImage/${id}`;
      });
      
      // Prepare the input for fileUpdate mutation
      const fileUpdateInputs = formattedMediaIds.map(mediaId => ({
        id: mediaId,
        referencesToRemove: [formattedProductId]
      }));
      
      const fileUpdateMutation = `
        mutation fileUpdate($files: [FileUpdateInput!]!) {
          fileUpdate(files: $files) {
            files {
              id
              alt
            }
            userErrors {
              field
              message
            }
          }
        }
      `;
      
      const fileUpdateVariables = {
        files: fileUpdateInputs
      };
      
      const result = await this.makeGraphQLRequest(fileUpdateMutation, fileUpdateVariables);
      
      if (result?.fileUpdate?.userErrors?.length > 0) {
        const errors = result.fileUpdate.userErrors;
        console.error('Error unlinking media:', errors);
        throw new Error(`Failed to unlink media: ${errors.map(e => e.message).join(', ')}`);
      }
      
      const unlinkedFiles = result?.fileUpdate?.files || [];
      console.log(`Successfully unlinked ${unlinkedFiles.length} media items from product ${formattedProductId}`);
      
      return {
        success: true,
        unlinkedCount: unlinkedFiles.length,
        unlinkedFiles
      };
    } catch (error) {
      console.error('Error unlinking media items:', error);
      throw error;
    }
  }

  /**
   * Static method for unlinking media items from a product
   * @param {string} productId - The ID of the product to unlink media from
   * @param {Array<string>} mediaIds - Array of media IDs to unlink
   * @returns {Promise<object>} Result of the unlinking operation
   */
  static async unlinkMediaFromProduct(productId, mediaIds) {
    const service = new ShopifyBulkOperationsService();
    return service.unlinkMediaFromProduct(productId, mediaIds);
  }
}

export default ShopifyBulkOperationsService;
